import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CatalogueItemResource,
  CatalogueItemsController_list_v1,
  type OutletResource,
  OutletsController_list_v1,
  TraceController_trace_v1,
  type TraceDayResource,
  type TraceEntryResource,
  type TraceSubjectResource,
  type UserResource,
  UsersController_list_v1,
} from '@repo/api-client';
import { Badge } from '@repo/ui/components/badge';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconSearch } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { collectPages, type ListPage } from '@/api/collect-pages';
import { isForbidden, isNotFound } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { describeRecordingGap, formatInstant } from '@/lib/instant';
import { formatPaise } from '@/lib/money';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { currentCursor, type CursorTrail, FIRST_PAGE } from '../back-office/cursor-pagination';
import { CursorPager } from '../back-office/CursorPager';
import { DayPicker } from '../back-office/DayPicker';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { RECEIPT_NUMBER_EXAMPLE, receiptNumberSchema } from '../sales/receipt-number';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import { TICKET_NUMBER_EXAMPLE, ticketNumberSchema } from '../tickets/ticket-number';
import {
  currentBusinessDate,
  defaultPeriod,
  periodProblem,
  type ReportingPeriod,
} from './reporting-period';
import { describeDayState, describeTraceEntry, labelForSource } from './trace-entries';
import {
  isBoundedMode,
  labelForTraceField,
  labelForTraceMode,
  TRACE_MODES,
  type TraceMode,
  traceQuery,
} from './trace-subject';

/**
 * One thing's whole timeline: `GET /v1/reports/trace`.
 *
 * **The investigation read, and the one a manager can reach.** Every other
 * Reporting screen answers "what happened over this period"; this one answers
 * "what happened to *this*" — a receipt somebody is holding, a ticket, a person's
 * shift, an item on a shelf — by drawing sales, basket lines, refunds, stock
 * movements, ledger entries and audit entries into one list ordered by the server
 * clock (FR-913).
 *
 * `trace-subject.ts` holds the two shapes this route has and why they must not be
 * conflated; `trace-entries.ts` holds how a row of six different tables is read.
 * What is left here is the screen.
 *
 * ## Three things this screen must get right, and they are the ticket's acceptance
 *
 * **A receipt number traces with no date range.** Not "the range is optional" —
 * `from` and `to` beside a subject are refused `400`. The rail therefore does not
 * *offer* pickers in an artefact mode, and `traceQuery` cannot emit the fields
 * even if it were handed a period. That is deliberate belt and braces: the
 * control not rendering is what a person sees, and the query not carrying it is
 * what a regression would have to get past.
 *
 * **A cursor goes back exactly as it came.** `cursor-pagination.ts` and
 * `CursorPager` are the pair FE049 established, and nothing on this screen reads,
 * splits or constructs a cursor. Ascending on an append-only key is what makes
 * that safe while the park is trading: a till writing mid-read appends *after*
 * the reader's position, so paging neither skips nor duplicates and there is
 * nothing to freeze and nothing to warn anybody about.
 *
 * **A day with no state renders without one.** See `trace-entries.ts` — on this
 * route that null is genuine and not the schema defect the item reads have.
 *
 * ## Ordering is the server's, and nothing here re-sorts
 *
 * Entries arrive oldest first by `recordedAt` with the record id breaking ties,
 * "because a sale, its lines and its ledger entry are written in one transaction
 * and routinely share a millisecond". `occurredAt` — the device clock — is shown
 * beside it where a device reported one, because a wide gap between the two is
 * itself evidence and is precisely what somebody investigating is looking for.
 * It is never sorted on (FR-008).
 *
 * ## More than one subject is an answer, not an error
 *
 * A receipt counter belongs to its till (R-402), so `482` off a torn receipt can
 * name a sale on every till in reach — and the route answers with each of them
 * rather than picking one. Every subject is rendered. **Nothing here reads
 * `subjects[0]`**, which is the defect `ReceiptLookup` was written around and the
 * same one is available here.
 *
 * The subject band shows the till's prefix and the counter as two facts rather
 * than joined into `LK-482`: the printed form is zero-padded and this route
 * publishes the prefix and the value, not the string on the paper. Assembling one
 * would be inventing a number that does not match what somebody is holding.
 *
 * ## What is deliberately not here
 *
 * **`saleId` and `ticketId`.** The route takes both and they reach the same two
 * artefacts the printed numbers already reach; `trace-subject.ts` says why a uuid
 * field is not a control a person can use, and what a later "Trace this sale"
 * link would carry instead.
 *
 * **A CSV export.** This route has none. Nothing here works around that.
 *
 * **A total.** A keyset page publishes none, and the day breakdown counts the
 * whole bounded timeline rather than the page on screen — which is why it does
 * not move as you page.
 *
 * **`guidance`.** Passing it declares the screen a form: `Screen` then caps the
 * children at a measure and pins a margin beside them. That is right for five
 * fields and wrong for a table — it was tried, and the rows stopped well short of
 * the field's edge with the amounts right-aligned against nothing. The sentence
 * that would have gone there is the `description`, which sets in the field's foot
 * on a list screen.
 */

/** Every state the timeline can be in. One value, so two cannot coexist. */
type TraceState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly subjects: readonly TraceSubjectResource[];
      readonly entries: readonly TraceEntryResource[];
      readonly days: readonly TraceDayResource[];
      readonly nextCursor: string | null;
    }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The "not narrowing" value. Not `''`, which Base UI clears to. */
const EVERY_OUTLET = 'every-outlet';

const COLUMNS = 5;

export function Trace() {
  const client = useApiClient();

  const [mode, setMode] = useState<TraceMode>('RECEIPT_NUMBER');

  // What is actually being traced. Set by the artefact form's submit or by a
  // bounded picker's choice — never by a keystroke, because a request per
  // character on an investigation read is a request per character.
  const [named, setNamed] = useState('');

  const [period, setPeriod] = useState<ReportingPeriod>(() => defaultPeriod(systemClock));
  const [outletId, setOutletId] = useState<string>(EVERY_OUTLET);
  const [trail, setTrail] = useState<CursorTrail>(FIRST_PAGE);
  const [state, setState] = useState<TraceState>({ kind: 'idle' });

  // A counter rather than a boolean, so two retries in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);
  const [people, setPeople] = useState<readonly UserResource[]>([]);
  const [items, setItems] = useState<readonly CatalogueItemResource[]>([]);

  const bounded = isBoundedMode(mode);

  // Only a bounded trace has a period to be wrong about. An artefact derives its
  // own, so there is nothing here to refuse.
  const problem = bounded ? periodProblem(period, systemClock) : null;

  const cursor = currentCursor(trail);

  // The park's own day, in Asia/Kolkata — not the device's. Read per render
  // rather than held, so a screen left open across midnight bounds itself at the
  // new day.
  const today = currentBusinessDate(systemClock);

  const query = useMemo(
    () => traceQuery({ subject: { mode, value: named }, period, outletId: undefined, cursor }),
    [mode, named, period, cursor],
  );

  /**
   * The outlet narrowing, applied separately.
   *
   * Kept out of the memo above so the "every outlet" sentinel never reaches
   * `traceQuery`, which takes the field's absence and not a magic value. A
   * narrowing is also meaningless on an artefact trace — the route refuses it
   * beside a subject — so it is only ever added on the bounded branch.
   */
  const narrowed = useMemo(() => {
    if (query === null) return null;

    if (!bounded || outletId === EVERY_OUTLET) return query;

    return { ...query, outletId };
  }, [query, bounded, outletId]);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole: outlets, people and the catalogue are what a picker needs in
    // one piece, and `docs/ui-conventions.md` gives `collectPages` for exactly
    // this. Retired and deactivated included — a trace is a read of the past, and
    // somebody who left last month still rang up sales the month before.
    const load = <T,>(read: (offset: number, limit: number) => Promise<ListPage<T>>) =>
      collectPages<T>(read).catch(() => [] as T[]);

    void Promise.all([
      load<OutletResource>((offset, limit) =>
        client.request(OutletsController_list_v1, {
          query: { offset, limit, includeRetired: true },
          signal: controller.signal,
        }),
      ),
      load<UserResource>((offset, limit) =>
        client.request(UsersController_list_v1, {
          query: { offset, limit, includeDeactivated: true },
          signal: controller.signal,
        }),
      ),
      load<CatalogueItemResource>((offset, limit) =>
        client.request(CatalogueItemsController_list_v1, {
          query: { offset, limit, includeRetired: true },
          signal: controller.signal,
        }),
      ),
    ]).then(([loadedOutlets, loadedPeople, loadedItems]) => {
      if (!live) return;

      // A failed picker read is a picker with nothing in it, not a failed screen:
      // the receipt and ticket paths need none of these three, and they are the
      // paths somebody at a counter is on.
      setOutlets(loadedOutlets);
      setPeople(loadedPeople);
      setItems(loadedItems);
    });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    // Nothing named, a half-filled range, or a range the screen can already see
    // is refused. None of the three is a request.
    if (narrowed === null || problem !== null) {
      setState({ kind: 'idle' });

      return;
    }

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(TraceController_trace_v1, { query: narrowed, signal: controller.signal })
      .then((answer) => {
        if (live) {
          setState({
            kind: 'loaded',
            subjects: answer.subjects,
            entries: answer.entries,
            // Absent means the breakdown was not asked for, which cannot happen
            // here — `byDay` is always on. An empty list is a different answer
            // and is rendered as one.
            days: answer.days ?? [],
            nextCursor: answer.nextCursor,
          });
        }
      })
      .catch((error: unknown) => {
        // An abort is this screen's own doing — a filter changed, or it
        // unmounted — and is not a failure to report.
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, narrowed, problem, reloads]);

  /**
   * Anything that changes *what* is being asked makes the trail worthless.
   *
   * A cursor names a position in one ordered result set; carried into a different
   * one it is at best meaningless and at worst the out-of-range instant
   * `blossom-rest-api#347` answers a retryable 500 for.
   */
  const restart = (apply: () => void) => {
    apply();
    setTrail(FIRST_PAGE);
  };

  // Base UI resolves what a trigger shows from `items` and not from the rendered
  // options, so a picker without it shows a bare UUID where a name belongs. Each
  // is derived from the same rows its options render.
  const modeItems = useMemo(
    () => TRACE_MODES.map((each) => ({ label: labelForTraceMode(each), value: each })),
    [],
  );

  const outletItems = useMemo(
    () => [
      { label: 'Every outlet', value: EVERY_OUTLET },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  const peopleItems = useMemo(
    () => people.map((person) => ({ label: person.displayName, value: person.id })),
    [people],
  );

  const catalogueItems = useMemo(
    () => items.map((item) => ({ label: item.name, value: item.id })),
    [items],
  );

  const rows = state.kind === 'loaded' ? state.entries : [];

  return (
    <Screen
      title="Trace"
      lede="what happened to one thing"
      description="One thing's whole timeline. A receipt or a ticket brings back everything connected to it — its basket, its refund, what they moved on a shelf, what they wrote to the books — and needs no dates, because the period comes from its own timeline. A person or an item is a search instead, so it takes a period and pages through it."
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="trace-mode" className="text-sm text-muted-foreground">
              Trace by
            </Label>
            <Select
              value={mode}
              items={modeItems}
              onValueChange={(next) => {
                restart(() => {
                  // Base UI types the value as nullable because a select may be
                  // cleared, which this one has no affordance for. Read back to
                  // the default rather than cast away.
                  setMode((next as TraceMode | null) ?? 'RECEIPT_NUMBER');
                  // What was named belonged to the old mode. A receipt number is
                  // not a person's id, and carrying it across would send one as
                  // the other.
                  setNamed('');
                });
              }}
            >
              <SelectTrigger id="trace-mode" aria-label="Trace by" className="h-10 w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {TRACE_MODES.map((each) => (
                    <SelectItem key={each} value={each}>
                      {labelForTraceMode(each)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Keyed on the mode so the field remounts: a fresh schema, an empty
              value and no error carried over from the number that was typed for
              a different kind of artefact. */}
          {!bounded && (
            <PrintedNumberField
              key={mode}
              mode={mode}
              onTrace={(value) => {
                restart(() => {
                  setNamed(value);
                });
              }}
            />
          )}

          {bounded && (
            <div className="space-y-2">
              <Label htmlFor="trace-subject" className="text-sm text-muted-foreground">
                {labelForTraceField(mode)}
              </Label>
              <Select
                value={named}
                items={mode === 'STAFF' ? peopleItems : catalogueItems}
                onValueChange={(next) => {
                  restart(() => {
                    setNamed(next ?? '');
                  });
                }}
              >
                <SelectTrigger
                  id="trace-subject"
                  aria-label={labelForTraceField(mode)}
                  disabled={(mode === 'STAFF' ? people : items).length === 0}
                  className="h-10 w-full bg-card font-normal"
                >
                  <SelectValue
                    placeholder={mode === 'STAFF' ? 'Choose somebody' : 'Choose an item'}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {mode === 'STAFF'
                      ? people.map((person) => (
                          <SelectItem key={person.id} value={person.id}>
                            {person.displayName}
                          </SelectItem>
                        ))
                      : items.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.name}
                          </SelectItem>
                        ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* The period and the outlet exist only on the bounded half of this
              route. On an artefact they are not "left empty" — they are refused
              beside a subject, so there is nothing here to leave empty. */}
          {bounded && (
            <>
              <div className="space-y-2">
                <Label htmlFor="trace-from" className="text-sm text-muted-foreground">
                  From
                </Label>
                <DayPicker
                  id="trace-from"
                  value={period.from}
                  emptyLabel="Choose a day"
                  format={formatBusinessDate}
                  fromDate={fromCalendarDate}
                  toDate={toCalendarDate}
                  maxDay={today}
                  onValueChange={(next) => {
                    restart(() => {
                      setPeriod((held) => ({ ...held, from: next }));
                    });
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="trace-to" className="text-sm text-muted-foreground">
                  To
                </Label>
                <DayPicker
                  id="trace-to"
                  value={period.to}
                  emptyLabel="Choose a day"
                  format={formatBusinessDate}
                  fromDate={fromCalendarDate}
                  toDate={toCalendarDate}
                  // The route refuses a period ending after the park's current
                  // trading day, so a day past it is not offered. The notice
                  // below still stands as the second line of defence.
                  maxDay={today}
                  onValueChange={(next) => {
                    restart(() => {
                      setPeriod((held) => ({ ...held, to: next }));
                    });
                  }}
                />
              </div>

              {/* Under the pickers rather than above the table: it is about the
                  two controls immediately above it, and it is not a failed
                  read. */}
              {problem !== null && (
                <p role="status" className="text-sm text-destructive">
                  {problem}
                </p>
              )}

              <div className="space-y-2">
                <Label htmlFor="trace-outlet" className="text-sm text-muted-foreground">
                  Outlet
                </Label>
                <Select
                  value={outletId}
                  items={outletItems}
                  onValueChange={(next) => {
                    restart(() => {
                      setOutletId(next ?? EVERY_OUTLET);
                    });
                  }}
                >
                  <SelectTrigger
                    id="trace-outlet"
                    aria-label="Outlet"
                    disabled={outlets.length === 0}
                    className="h-10 w-full bg-card"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={EVERY_OUTLET}>Every outlet</SelectItem>
                      {outlets.map((outlet) => (
                        <SelectItem key={outlet.id} value={outlet.id}>
                          {outlet.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {state.kind === 'loaded' && (
            <ScreenFact label="Period">
              {/* The envelope's, and on an artefact trace it is the answer rather
                  than the question: the period is derived from the days the
                  subject's own timeline falls in. */}
              {state.days.length === 0 ? (
                'No trading day carries any of this.'
              ) : (
                <>
                  {formatBusinessDate(state.days[0]?.businessDate ?? '')} to{' '}
                  {formatBusinessDate(state.days[state.days.length - 1]?.businessDate ?? '')}
                  <br />
                  <span className="text-muted-foreground">
                    {bounded
                      ? 'The days asked for, by the trading day each record fell in.'
                      : 'Taken from this subject’s own timeline, not from a period asked for.'}
                  </span>
                </>
              )}
            </ScreenFact>
          )}
        </>
      }
    >
      {state.kind === 'loaded' && state.subjects.length > 0 && (
        <SubjectBand subjects={state.subjects} />
      )}

      {state.kind === 'loaded' && state.days.length > 0 && <DayBand days={state.days} />}

      {/* `stated`, with a width on all five. Both were rendered: `measured` gives
          every scrap of slack to the one unconstrained column, and at 2000 that
          put about 700px of nothing between "Basket line" and the person who did
          it. Stating them all lets fixed layout share the slack out in
          proportion, which is the same finding and the same fix `CashBook` and
          `StockMovements` record — and Amount still ends on the field's right
          edge, which is where the figures are read down. */}
      <ScreenTable label="Timeline" columns="stated">
        <TableHeader>
          <TableRow>
            <TableHead className="w-52">When</TableHead>
            <TableHead className="w-56">What</TableHead>
            <TableHead className="w-36">Who</TableHead>
            <TableHead className="w-20 text-right">Count</TableHead>
            <TableHead className="w-32 text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>

        {state.kind === 'idle' && (
          <TableNotice columns={COLUMNS}>
            <EmptyState
              title={bounded ? 'Choose who or what to trace' : 'Enter a number to trace'}
              description={
                bounded
                  ? 'A trace over a person or an item is a search over a period. Choose the subject and the two days that bound it.'
                  : 'Type the number printed on the receipt or the ticket. Everything connected to it comes back — no dates needed, because the period comes from its own timeline.'
              }
            />
          </TableNotice>
        )}

        {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

        {state.kind === 'failed' && (
          <TableNotice columns={COLUMNS}>
            {isForbidden(state.error) ? (
              <EmptyState
                title="This account cannot trace anything"
                description="The trace is the owner's and the manager's. If that is what you are, sign out and back in — the role this browser is holding may be out of date."
              />
            ) : isNotFound(state.error) ? (
              // The route answers 404 identically for an artefact nobody printed
              // and one at an outlet outside this caller's reach, deliberately,
              // because a refusal that told them apart is an enumeration oracle.
              // So the copy says neither, and there is no branch here that could.
              <EmptyState
                title="Nothing to trace under that number"
                description="No receipt or ticket in reach carries it. Read it off the paper again — the letters before the hyphen name the till, and they are as easy to misread as the digits."
              />
            ) : (
              <LoadFailed
                error={state.error}
                onRetry={() => {
                  setReloads((count) => count + 1);
                }}
                title="That trace could not be read"
              />
            )}
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (rows.length === 0 ? (
            <TableNotice columns={COLUMNS}>
              <EmptyState
                title="Nothing happened"
                description={
                  bounded
                    ? 'Nothing was recorded for this subject between these two days. Widen the period, or try every outlet. An empty timeline is an answer, not a refusal.'
                    : 'This exists, and nothing else is connected to it yet.'
                }
              />
            </TableNotice>
          ) : (
            <TableBody>
              {/* In the order the API sent them — oldest first by the server
                  clock with the record id breaking ties. Nothing here sorts,
                  filters, nets a reversal off, or adds a row. */}
              {rows.map((row) => (
                <EntryRow key={`${row.source}-${row.id}`} entry={row} />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {/* Only on the paged half, and only when the read succeeded. An artefact
          trace returns its whole timeline in one response and publishes
          `nextCursor: null` for it, so there is nothing here to step through. */}
      {bounded && state.kind === 'loaded' && (
        <CursorPager
          trail={trail}
          rows={rows.length}
          nextCursor={state.nextCursor}
          onTrailChange={setTrail}
          label="timeline"
          noun="entry"
        />
      )}
    </Screen>
  );
}

/**
 * The number off the paper, and the one control that asks for it.
 *
 * A form and not a filter: `docs/ui-conventions.md` is explicit that every form
 * in this app is `react-hook-form` + `zod` behind the registry's `Form`, and this
 * is a field somebody types into and submits. The two schemas are the ones the
 * lookups already use — `receiptNumberSchema` and `ticketNumberSchema` — imported
 * rather than re-typed, which is the rule that stops the shape of a receipt
 * number being defined twice and drifting.
 *
 * The two are **not** interchangeable, and the route says so: a receipt's counter
 * half on its own is accepted, off paper whose top line is torn, because the
 * counter belongs to a till and the answer then names every sale that carries it.
 * A ticket number's whole display form is required, because every gate's counter
 * reaches the same values and a bare counter names no artefact at all.
 * `isPrintedReceiptNumber` and `isPrintedTicketNumber` already differ on exactly
 * that, so choosing the schema is the whole of the difference here.
 */
function PrintedNumberField({
  mode,
  onTrace,
}: {
  readonly mode: TraceMode;
  onTrace: (value: string) => void;
}) {
  const receipt = mode === 'RECEIPT_NUMBER';

  const schema = useMemo(
    () => z.object({ value: receipt ? receiptNumberSchema : ticketNumberSchema }),
    [receipt],
  );

  const form = useForm({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: { value: '' },
  });

  return (
    <Form {...form}>
      <form
        noValidate
        className="space-y-2"
        onSubmit={(event) =>
          void form.handleSubmit((values) => {
            onTrace(values.value);
          })(event)
        }
      >
        <FormField
          control={form.control}
          name="value"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                {labelForTraceField(mode)}
              </FormLabel>
              <FormControl
                render={
                  <Input
                    // A number read aloud off paper and typed back in, so it gets
                    // the mono face and tabular figures — the same treatment the
                    // lookups give it, so the two look like the same fact.
                    className={`${fieldSurface} h-10 font-mono numeric`}
                    placeholder={receipt ? RECEIPT_NUMBER_EXAMPLE : TICKET_NUMBER_EXAMPLE}
                    // Nothing here is a name or an address, and a browser
                    // offering last week's numbers is in the way of somebody
                    // reading this one off paper.
                    autoComplete="off"
                    spellCheck={false}
                    required
                    {...field}
                  />
                }
              />
              <FormDescription>
                {receipt
                  ? 'As printed, including the letters — they name the till. The number on its own works when the top of the receipt is gone, and traces every sale that carries it.'
                  : 'The whole printed form, including the letters. A ticket counter on its own names nothing, because every gate reaches the same values.'}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* The screen's one accent. Nothing else here writes or navigates. */}
        <Button type="submit" className="w-full">
          <IconSearch data-icon="inline-start" />
          Trace
        </Button>
      </form>
    </Form>
  );
}

/**
 * What the trace is anchored on.
 *
 * **Every subject, never the first one.** A counter belongs to its till, so a
 * bare `482` can name a sale on each till in reach and the route answers with all
 * of them rather than choosing. Rendering one would tell somebody about another
 * customer's basket for another customer's money.
 *
 * Absent entirely on a bounded trace, which is anchored on a person or a product
 * rather than on an artefact and publishes an empty list for it.
 */
function SubjectBand({ subjects }: { readonly subjects: readonly TraceSubjectResource[] }) {
  return (
    <section
      aria-label="What is being traced"
      className="-mx-4 border-b border-border px-4 pb-4 lg:-mx-7 lg:px-7"
    >
      {subjects.length > 1 && (
        <p className="mb-3 text-sm text-muted-foreground">
          {subjects.length} artefacts carry that number. A counter belongs to the till that printed
          it, so more than one till in reach has reached this one — the prefix below is what tells
          them apart.
        </p>
      )}

      <ul className="flex flex-wrap gap-x-8 gap-y-3">
        {subjects.map((each) => (
          <li key={each.id} className="text-sm">
            {/* The prefix and the counter named separately, not joined into
                `LK-482`: the printed form is zero-padded and this route publishes
                the prefix and the value rather than the string on the paper, so
                assembling one would show a number that does not match what
                somebody is holding. */}
            <span className="text-foreground">{each.kind === 'SALE' ? 'Sale' : 'Ticket'}</span>
            <span className="text-muted-foreground"> on till </span>
            <span className="font-mono text-foreground numeric">{each.terminalPrefix}</span>
            <span className="text-muted-foreground">, number </span>
            <span className="font-mono text-foreground numeric">{each.numberValue}</span>
            <br />
            <span className="text-muted-foreground">
              {each.outletName}, {formatBusinessDate(each.businessDate)}, {each.actorName} ·{' '}
            </span>
            <span className="font-mono text-foreground numeric">
              {formatPaise(each.amountPaise)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * How much of this timeline fell on each trading day.
 *
 * **It counts the whole bounded timeline, not the page on screen** — a breakdown
 * of one page would change as you paged and would say a person was active on
 * three days when the answer is thirty. So it does not move under the pager.
 *
 * The counts can add up to fewer than the timeline holds, and that is not a
 * defect: an audit entry belongs to no trading day, so it appears in the list
 * above and in no bucket here. Inventing a day for it from when it was recorded
 * would cut the day at midnight UTC — half past five in the morning in the park —
 * and disagree with the reconciliation for that day.
 *
 * A day with **no state** is the one this band exists to get right. See
 * `trace-entries.ts`: the date and its count are shown, and no badge is drawn.
 */
function DayBand({ days }: { readonly days: readonly TraceDayResource[] }) {
  return (
    <section
      aria-label="Trading days"
      className="-mx-4 border-b border-border px-4 py-4 lg:-mx-7 lg:px-7"
    >
      <ul className="flex flex-wrap gap-2">
        {days.map((day) => {
          const settlement = describeDayState(day.state);

          return (
            <li
              key={day.businessDate}
              // Named, so the pair a reader hears is the date and its figures
              // rather than "list item".
              aria-label={formatBusinessDate(day.businessDate)}
              className="flex items-center gap-2 rounded-2xl border border-border px-3 py-1.5 text-sm"
            >
              <span className="text-foreground numeric">
                {formatBusinessDate(day.businessDate)}
              </span>
              {/* One text node, not a styled count beside a word: "1 entry" is a
                  phrase somebody reads whole, and splitting it puts a line break
                  opportunity in the middle of a chip. */}
              <span className="font-mono text-muted-foreground numeric">
                {`${String(day.entryCount)} ${day.entryCount === 1 ? 'entry' : 'entries'}`}
              </span>
              {/* Nothing at all where there is no state. A date can carry
                  activity that no outlet opened a day on — reachable through a
                  reprint, dated from its own recorded instant — and it is
                  stateless rather than OPEN, because nothing is in progress. Not
                  a dash either: a dash in a column of states reads as a zero. */}
              {settlement !== null && (
                <Badge variant={settlement.variant}>{settlement.label}</Badge>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** One row of the timeline. */
function EntryRow({ entry }: { readonly entry: TraceEntryResource }) {
  const read = describeTraceEntry(entry);

  const gap =
    entry.occurredAt === null ? null : describeRecordingGap(entry.occurredAt, entry.recordedAt);

  return (
    <TableRow>
      <TableCell className="py-3.5 align-top">
        {/* The server clock, which is what the list is ordered by (FR-009). */}
        <span className="numeric">{formatInstant(entry.recordedAt)}</span>
        <br />
        <span className="text-xs text-muted-foreground">
          {read.businessDate === null ? (
            // An audit entry carries no business day, so there is nothing to
            // resolve and nothing is invented for it.
            'No trading day'
          ) : (
            <span className="numeric">{formatBusinessDate(read.businessDate)}</span>
          )}
        </span>
        {/* The device clock, and **only when it disagrees**. Both instants are
            published rather than reconciled because a wide gap between them is
            itself evidence — which is exactly what somebody investigating is
            looking for — but printing the till's clock on every row would set the
            same date twice under three quarters of them and bury the rows where
            it differs. `describeRecordingGap` is the module that already decides
            what is worth saying; it answers `null` under its own threshold. The
            device clock still orders nothing (FR-008). */}
        {gap !== null && (
          <>
            <br />
            <span className="text-xs text-muted-foreground">{gap}</span>
          </>
        )}
      </TableCell>

      <TableCell className="py-3.5 align-top">
        <span className="text-foreground">{read.what}</span>
        <br />
        <span className="text-xs text-muted-foreground">{labelForSource(entry.source)}</span>
        {read.reverses && (
          <>
            {' '}
            {/* `outline` and not `destructive`. Both halves of a reversal appear
                and neither is netted away (FR-715); a correction that already
                happened is not the system refusing something. */}
            <Badge variant="outline">Reverses another</Badge>
          </>
        )}
      </TableCell>

      <TableCell className="py-3.5 align-top">{entry.actorName}</TableCell>

      <TableCell className="py-3.5 text-right align-top">
        {read.quantity === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="font-mono numeric">{read.quantity}</span>
        )}
      </TableCell>

      <TableCell className="py-3.5 text-right align-top">
        {read.amount === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <>
            <span className="font-mono numeric">{read.amount}</span>
            {/* The direction is its own field and the amount is a magnitude, so a
                debit is never rendered as a negative. A reader adding this column
                would otherwise get a number the API never published. */}
            {read.direction !== null && (
              <>
                <br />
                <span className="text-xs text-muted-foreground">{read.direction}</span>
              </>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );
}
